import React, { useState } from 'react';
import { FileText, Download, Eye, Calendar, Filter, Plus, Trash2, RefreshCw } from 'lucide-react';
import { Button } from '../../../components/ui/button.js';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card.js';


interface MemoryItem {
  id: string;
  text: string;
  tags?: string[];
  sessionId?: string;
  metadata?: {
    source?: string;
    createdAt?: string;
  };
}

interface TranscriptsTabProps {
  searchTerm: string;
  selectedAgent: string;
  selectedType: string;
  items: MemoryItem[];
  loading: boolean;
  error: string | null;
  onDelete: (itemId: string) => void;
  onRefresh: () => void;
}

const TranscriptsTab: React.FC<TranscriptsTabProps> = ({ searchTerm, selectedAgent, selectedType, items, loading, error, onDelete, onRefresh }) => {
  const transcripts = items.map(item => ({
    id: item.id,
    title: item.text ? (item.text.length > 60 ? item.text.substring(0, 60) + '...' : item.text) : 'Untitled Transcript',
    description: item.text || 'No description available',
    sessionId: item.sessionId || 'unknown',
    agent: item.metadata?.source || 'System Agent',
    type: 'Session',
    duration: '1h 30m',
    date: item.metadata?.createdAt ? new Date(item.metadata.createdAt).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
    status: 'Completed',
    tags: item.tags || [],
    fileSize: '1.2 MB',
    taskCount: Math.floor(Math.random() * 50) + 10,
    successRate: Math.floor(Math.random() * 20) + 80
  }));

  const [filter, setFilter] = useState('all');
  const [sortBy, setSortBy] = useState('recent');

  const filteredTranscripts = transcripts.filter(transcript => {
    const matchesSearch = transcript.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         transcript.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         transcript.tags.some((tag: string) => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesAgent = selectedAgent === 'All' || transcript.agent === selectedAgent;
    const matchesType = selectedType === 'All' || transcript.type === selectedType;
    const matchesFilter = filter === 'all' || transcript.status.toLowerCase() === filter;
    
    return matchesSearch && matchesAgent && matchesType && matchesFilter;
  });

  const sortedTranscripts = [...filteredTranscripts].sort((a, b) => {
    switch (sortBy) {
      case 'duration':
        return b.taskCount - a.taskCount;
      case 'success':
        return b.successRate - a.successRate;
      case 'recent':
      default:
        return new Date(b.date).getTime() - new Date(a.date).getTime();
    }
  });

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed':
        return 'bg-green-400/20 text-green-400';
      case 'in-progress':
        return 'bg-yellow-400/20 text-yellow-400';
      case 'failed':
        return 'bg-red-400/20 text-red-400';
      default:
        return 'bg-slate-400/20 text-slate-400';
    }
  };

  const getTypeColor = (type: string) => {
    switch (type.toLowerCase()) {
      case 'migration':
        return 'bg-purple-400/20 text-purple-400';
      case 'bug fix':
        return 'bg-red-400/20 text-red-400';
      case 'feature':
        return 'bg-blue-400/20 text-blue-400';
      case 'optimization':
        return 'bg-orange-400/20 text-orange-400';
      default:
        return 'bg-slate-400/20 text-slate-400';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-8 h-8 animate-spin text-slate-400" />
        <span className="ml-3 text-slate-400">Loading transcripts...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="text-red-400 mb-4">⚠️ {error}</div>
        <Button onClick={onRefresh} variant="ghost">
          <RefreshCw size={16} className="mr-2" />
          Try Again
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gradient text-drop-shadow">Transcripts</h2>
          <p className="text-slate-400 mt-1">Session logs and execution transcripts ({transcripts.length} entries)</p>
        </div>
        <div className="flex space-x-2">
          <Button size="sm" variant="ghost" onClick={onRefresh}>
            <RefreshCw size={16} className="mr-2" />
            Refresh
          </Button>
          <Button size="sm">
            <Plus size={16} className="mr-2" />
            Upload Transcript
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          <Filter size={16} className="text-slate-400" />
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="violett-input"
          >
            <option value="all">All Status</option>
            <option value="completed">Completed</option>
            <option value="in-progress">In Progress</option>
            <option value="failed">Failed</option>
          </select>
        </div>
        
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          className="violett-input"
        >
          <option value="recent">Most Recent</option>
          <option value="duration">Longest Duration</option>
          <option value="success">Highest Success Rate</option>
        </select>
      </div>

      <div className="grid grid-cols-1 gap-4">
        {sortedTranscripts.map((transcript) => (
          <Card key={transcript.id} className="bg-slate-900/40 backdrop-blur-sm border border-slate-700/30 rounded-xl p-6 hover:border-slate-600/50 transition-all duration-300 cursor-pointer group hover:scale-105">
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <CardTitle className="text-lg cream-gradient-text text-drop-shadow group-hover:text-gradient transition-colors mb-2">{transcript.title}</CardTitle>
                  <CardDescription className="text-slate-400 cream-gradient-text text-drop-shadow">{transcript.description}</CardDescription>
                </div>
                <div className="flex items-center space-x-2 ml-4">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(transcript.status)}`}>
                    {transcript.status}
                  </span>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getTypeColor(transcript.type)}`}>
                    {transcript.type}
                  </span>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <span className="text-slate-400">Agent:</span>
                  <div className="text-brand-text font-medium">{transcript.agent}</div>
                </div>
                <div>
                  <span className="text-slate-400">Duration:</span>
                  <div className="text-brand-text font-medium">{transcript.duration}</div>
                </div>
                <div>
                  <span className="text-slate-400">Tasks:</span>
                  <div className="text-brand-text font-medium">{transcript.taskCount}</div>
                </div>
                <div>
                  <span className="text-slate-400">Success Rate:</span>
                  <div className="text-brand-text font-medium">{transcript.successRate}%</div>
                </div>
              </div>

              <div className="flex flex-wrap gap-1">
                {transcript.tags.map((tag: string) => (
                  <span key={tag} className="glass-dark bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl border border-slate-700/30 rounded-xl hover:border-slate-600/50 transition-all duration-300 px-2 py-1 text-slate-300 text-xs">
                    #{tag}
                  </span>
                ))}
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4 text-xs text-slate-400">
                  <div className="flex items-center space-x-1">
                    <Calendar size={12} />
                    <span>{transcript.date}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <FileText size={12} />
                    <span>{transcript.fileSize}</span>
                  </div>
                  <span>Session: {transcript.sessionId}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Button variant="ghost" size="sm">
                    <Eye size={14} className="mr-1" />
                    View
                  </Button>
                  <Button variant="ghost" size="sm">
                    <Download size={14} className="mr-1" />
                    Download
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(transcript.id);
                    }}
                    className="text-red-400 hover:text-red-300 hover:bg-red-400/10"
                  >
                    <Trash2 size={14} />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {sortedTranscripts.length === 0 && (
        <div className="text-center py-12">
          <FileText size={48} className="mx-auto text-slate-400 mb-4" />
          <h3 className="text-lg font-medium text-slate-300 mb-2">No transcripts found</h3>
          <p className="text-slate-400">Try adjusting your search or filters</p>
        </div>
      )}
    </div>
  );
};

export default TranscriptsTab;
